'use client';

import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { GlassButton } from '../GlassButton/GlassButton';
import { ShimmerLoader } from '../ShimmerLoader/ShimmerLoader';

export interface DocumentRendererProps {
  /** Base64-encoded document content */
  content: string | null;
  /** MIME type of the document */
  mimeType: string;
  /** File name for alt text and fallback display */
  fileName: string;
  /** Show loading skeleton */
  loading?: boolean;
  /** Callback when download is clicked (for unsupported types) */
  onDownload?: () => void;
}

export function DocumentRenderer({ content, mimeType, fileName, loading, onDownload }: DocumentRendererProps) {
  const { t } = useI18n();

  const dataUrl = useMemo(() => (content ? `data:${mimeType};base64,${content}` : null), [content, mimeType]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
        }}
      >
        <ShimmerLoader variant="rectangular" width="60%" height="80%" />
      </Box>
    );
  }

  if (!dataUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          gap: 2,
        }}
      >
        <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
        <Typography
          sx={{
            color: 'text.secondary',
          }}
        >
          {t('documents.view.noContent')}
        </Typography>
      </Box>
    );
  }

  // PDF rendering
  if (mimeType === 'application/pdf') {
    return (
      <Box sx={{ width: '100%', height: '100%', minHeight: 400 }}>
        <iframe src={dataUrl} title={fileName} style={{ width: '100%', height: '100%', border: 'none' }} />
      </Box>
    );
  }

  // Image rendering
  if (mimeType.startsWith('image/')) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 400,
          p: 2,
        }}
      >
        <Box
          component="img"
          src={dataUrl}
          alt={fileName}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
      </Box>
    );
  }

  // Unsupported type fallback
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 400,
        gap: 2,
      }}
    >
      <DescriptionIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
      <Typography
        sx={{
          color: 'text.secondary',
        }}
      >
        {t('documents.view.unsupported')}
      </Typography>
      {onDownload && (
        <GlassButton variant="outlined" startIcon={<DownloadIcon />} onClick={onDownload}>
          {t('documents.view.downloadToView')}
        </GlassButton>
      )}
    </Box>
  );
}
